import { Flex } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import type React from 'react';
import styled, { keyframes } from 'styled-components';
import { PetLandingPageType } from '../shared';
import {
  STORY_HEIGHT,
  STORY_HEIGHT_MOBILE,
  STORY_MARGIN,
  STORY_MARGIN_MOBILE,
  STORY_WIDTH,
  STORY_WIDTH_MOBILE,
  type Story,
  StoryCard,
} from './StoryCard';
import { CAT_STORIES, DOG_STORIES, PET_STORIES } from './defaultStories';

const carouselAnimation = keyframes`
    0% {
        transform: translateX(-23%);
    }

    100% {
        transform: translateX(-123%);
    }
`;

const DURATION_PER_STORY = 4100;

const Wrapper = styled.div`
  width: 100%;
  position: relative;
  isolation: isolate;
  overflow: hidden;
  height: ${STORY_HEIGHT_MOBILE + 70}px;

  ${MEDIA_QUERIES.tabletAndDesktop`
        height: ${STORY_HEIGHT + 100}px;
    `}
`;

const Carousel = styled(Flex)<{
  numOfStories: number;
  durationPerStory: number;
}>`
  align-self: start;
  height: ${STORY_HEIGHT}px;
  width: ${({ numOfStories }) => `${numOfStories * (STORY_WIDTH + STORY_MARGIN * 2)}px`};
  animation: ${carouselAnimation} linear infinite;
  animation-duration: ${({ durationPerStory, numOfStories }) => `${durationPerStory * numOfStories}ms`};

  ${MEDIA_QUERIES.desktop`
        &:hover {
            animation-play-state: paused;
        }
    `}

  ${MEDIA_QUERIES.mobile`
        top: 15px;
        animation-duration: 33000ms;
        height: ${STORY_HEIGHT_MOBILE}px;
        width: ${({ numOfStories }: { numOfStories: number }) =>
          `${numOfStories * (STORY_WIDTH_MOBILE + STORY_MARGIN_MOBILE * 2)}px`};
    `}
`;

const Overlay = styled.div`
  position: absolute;
  background: linear-gradient(to right, #fff, rgba(255, 255, 255, 0) 15%, rgba(255, 255, 255, 0) 85%, #fff);
  right: 0;
  width: 100%;
  z-index: 1;
  display: none;
  pointer-events: none;
  height: ${STORY_HEIGHT_MOBILE}px;

  ${MEDIA_QUERIES.tabletAndDesktop`
        display: block;
        height: ${STORY_HEIGHT}px;
    `}
`;

interface StoriesCarouselProps {
  durationPerStory?: number;
  petLandingPageType?: PetLandingPageType;
  stories?: Story[];
}

const StoriesCarousel: React.FC<StoriesCarouselProps> = ({
  durationPerStory = DURATION_PER_STORY,
  petLandingPageType = PetLandingPageType.PetInsurance,
  stories: externalStories,
}) => {
  const stories: Story[] =
    externalStories != null
      ? externalStories
      : petLandingPageType === PetLandingPageType.Dog
        ? DOG_STORIES
        : petLandingPageType === PetLandingPageType.Cat
          ? CAT_STORIES
          : PET_STORIES;

  return (
    <Wrapper>
      <Overlay />
      <Carousel numOfStories={stories.length} durationPerStory={durationPerStory}>
        {stories.concat(stories).map((story, index) => (
          <div key={story.name + index}>
            <StoryCard {...story} />
          </div>
        ))}
      </Carousel>
    </Wrapper>
  );
};

export default StoriesCarousel;
